import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { Typography, ButtonPro } from 'components';
import { agent } from 'services/service';
import {
  AttendanceImportConfirmResult,
  AttendanceExcelUploadResult
} from 'services/AttendanceTrackingService';
import { toast } from 'react-toastify';
import { formatAttendanceImportPeriod } from 'utils/formatAttendanceLastImport';
import './index.scss';

type UploadStep = 'upload' | 'review' | 'success';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const STEPS: { key: UploadStep; label: string }[] = [
  { key: 'upload', label: 'Upload files' },
  { key: 'review', label: 'Review summary' },
  { key: 'success', label: 'Complete' }
];

const ACCEPTED_EXTENSIONS = ['.xls', '.xlsx'];
const MAX_FILE_SIZE = agent.AttendanceTrackingService.maxFileSizeBytes;
const LARGE_BATCH_FILE_THRESHOLD =
  agent.AttendanceTrackingService.largeBatchFileThreshold;

const formatElapsed = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
};

const useElapsedSeconds = (isRunning: boolean) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isRunning) {
      setElapsedSeconds(0);
      return undefined;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isRunning]);

  return elapsedSeconds;
};

const formatFileSize = (bytes: number) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} bytes`;
};

const formatStatusClass = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized.includes('unknown')) return 'unknown';
  if (normalized.includes('duplicate')) return 'duplicate';
  if (normalized.includes('invalid')) return 'invalid';
  return 'valid';
};

const formatStatusLabel = (status: string) =>
  status.replace(/([A-Z])/g, ' $1').trim();

const stepIndex = (step: UploadStep) => STEPS.findIndex((s) => s.key === step);

interface ReviewMetric {
  index?: number;
  label: string;
  value: string;
  variant: 'new' | 'total' | 'dup' | 'warn';
}

const renderMetricCard = ({ index, label, value, variant }: ReviewMetric) => (
  <div
    className={`attendance-upload__metric attendance-upload__metric--${variant}`}
  >
    <div className="attendance-upload__metric-body">
      {index != null && (
        <span className="attendance-upload__metric-index" aria-hidden>
          {index}
        </span>
      )}
      <Typography
        type="xs_medium"
        text={label}
        weight="bold"
        className="attendance-upload__metric-label"
      />
      <Typography
        type="heading4"
        text={value}
        weight="bold"
        className="attendance-upload__metric-value"
      />
    </div>
  </div>
);

export const AttendanceUploadForm = ({
  onImportConfirmed
}: {
  onImportConfirmed?: () => void;
}) => {
  const [step, setStep] = useState<UploadStep>('upload');
  const [files, setFiles] = useState<File[]>([]);
  const [uploadResult, setUploadResult] =
    useState<AttendanceExcelUploadResult | null>(null);
  const [confirmResult, setConfirmResult] =
    useState<AttendanceImportConfirmResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const uploadRequestId = useRef(0);
  const analyzeElapsedSeconds = useElapsedSeconds(isAnalyzing);
  const confirmElapsedSeconds = useElapsedSeconds(isConfirming);
  const isBusy = isAnalyzing || isConfirming;

  useEffect(() => {
    if (!isBusy) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isBusy]);

  const onDrop = useCallback((accepted: File[], rejected: any[]) => {
    if (rejected.length > 0) {
      const firstError = rejected[0]?.errors?.[0];
      if (firstError?.code === 'file-too-large') {
        toast.error('Each file must be 10 MB or smaller.');
      } else if (firstError?.code === 'file-invalid-type') {
        toast.error('Only .xls and .xlsx attendance sheet files are allowed.');
      } else {
        toast.error('One or more files could not be added.');
      }
    }

    if (accepted.length > 0) {
      setConfirmResult(null);
      setUploadResult(null);
      setStep('upload');
      setFiles((prev) => {
        const names = new Set(prev.map((f) => f.name));
        const merged = [...prev];
        accepted.forEach((file) => {
          if (!names.has(file.name)) merged.push(file);
        });
        return merged;
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } =
    useDropzone({
      onDrop,
      accept: {
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
          '.xlsx'
        ]
      },
      maxSize: MAX_FILE_SIZE,
      multiple: true,
      noClick: true,
      noKeyboard: true,
      disabled: isAnalyzing || isConfirming || step !== 'upload'
    });

  const removeFile = (name: string) => {
    setUploadResult(null);
    setConfirmResult(null);
    setStep('upload');
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const handleContinue = async () => {
    if (files.length === 0) {
      toast.error('Please add at least one Excel file.');
      return;
    }

    const requestId = ++uploadRequestId.current;
    setIsAnalyzing(true);

    try {
      const response = await agent.AttendanceTrackingService.uploadExcel(files);

      if (requestId !== uploadRequestId.current) return;

      setUploadResult(response.data);
      setStep('review');
    } catch (err: any) {
      if (requestId !== uploadRequestId.current) return;

      const message =
        err.code === 'ECONNABORTED'
          ? 'Validation is taking longer than expected. Try uploading fewer files at once or check your connection.'
          : err.response?.data?.message ||
            err.message ||
            'Failed to validate the uploaded files.';
      toast.error(message);
    } finally {
      if (requestId === uploadRequestId.current) {
        setIsAnalyzing(false);
      }
    }
  };

  const handleConfirm = async () => {
    if (!uploadResult?.importToken) return;

    setIsConfirming(true);
    try {
      const response = await agent.AttendanceTrackingService.confirmImport(
        uploadResult.importToken
      );
      setConfirmResult(response.data);
      setStep('success');
      onImportConfirmed?.();
      toast.success(response.data.message);
    } catch (err: any) {
      const message =
        err.code === 'ECONNABORTED'
          ? 'Import is taking longer than expected. The upload may still be processing on the server.'
          : err.response?.data?.message ||
            err.message ||
            'Failed to import attendance data.';
      toast.error(message);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleStartOver = () => {
    uploadRequestId.current += 1;
    setIsAnalyzing(false);
    setFiles([]);
    setUploadResult(null);
    setConfirmResult(null);
    setStep('upload');
  };

  const handleBackToUpload = () => {
    uploadRequestId.current += 1;
    setIsAnalyzing(false);
    setUploadResult(null);
    setConfirmResult(null);
    setStep('upload');
  };

  const detectedPeriod = uploadResult?.detectedPeriodLabel ?? '';

  const issueRows =
    uploadResult?.sampleRows.filter((row) => row.status !== 'Valid') ?? [];

  const reviewMetrics: ReviewMetric[] = uploadResult
    ? [
        {
          index: 1,
          label: 'New punches',
          value: String(uploadResult.summary.validRows),
          variant: 'new'
        },
        {
          index: 2,
          label: 'Total rows',
          value: String(uploadResult.summary.totalRowsRead),
          variant: 'total'
        },
        {
          index: 3,
          label: 'Already imported',
          value: String(uploadResult.summary.duplicateInDatabase),
          variant: 'dup'
        },
        {
          index: 4,
          label: 'Unknown IDs',
          value: String(uploadResult.summary.unknownPersonnelIds),
          variant: 'warn'
        }
      ]
    : [];

  const currentStepIndex = stepIndex(step);
  const isLargeBatch = files.length >= LARGE_BATCH_FILE_THRESHOLD;

  const analyzingMessage = isLargeBatch
    ? `Validating ${files.length} files...`
    : 'Validating files...';

  const analyzingHint = isLargeBatch
    ? `Processing a large batch can take a few minutes. Elapsed: ${formatElapsed(analyzeElapsedSeconds)}`
    : `Matching personnel IDs and checking for duplicates. Elapsed: ${formatElapsed(analyzeElapsedSeconds)}`;

  const confirmingHint = `Saving attendance punches. Elapsed: ${formatElapsed(confirmElapsedSeconds)}`;

  return (
    <div className="attendance-upload">
      <nav className="attendance-upload__progress" aria-label="Upload progress">
        {STEPS.map((s, index) => {
          const isDone = index < currentStepIndex;
          const isActive = index === currentStepIndex;

          return (
            <React.Fragment key={s.key}>
              <div
                className={`attendance-upload__progress-step ${
                  isActive ? 'attendance-upload__progress-step--active' : ''
                } ${isDone ? 'attendance-upload__progress-step--done' : ''}`}
              >
                <span className="attendance-upload__progress-dot">
                  {isDone ? '✓' : index + 1}
                </span>
                <span className="attendance-upload__progress-label">
                  {s.label}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`attendance-upload__progress-line ${
                    isDone ? 'attendance-upload__progress-line--done' : ''
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {step === 'upload' && (
        <>
          <section className="attendance-upload__card">
            <div className="attendance-upload__card-header">
              <span className="attendance-upload__card-icon" aria-hidden>
                <UploadFileOutlinedIcon fontSize="small" />
              </span>
              <div>
                <Typography
                  type="heading4"
                  text="Attendance sheet files"
                  weight="bold"
                />
                <Typography
                  type="small_regular"
                  text="Add one or more weekly fingerprint exports to validate before import."
                  className="attendance-upload__card-hint"
                />
              </div>
            </div>

            <div
              {...getRootProps()}
              className={`attendance-upload__dropzone ${
                isDragActive ? 'attendance-upload__dropzone--active' : ''
              } ${isDragReject ? 'attendance-upload__dropzone--reject' : ''} ${
                isAnalyzing ? 'attendance-upload__dropzone--loading' : ''
              }`}
            >
              <input {...getInputProps()} />

              {isAnalyzing ? (
                <div className="attendance-upload__dropzone-body">
                  <CircularProgress
                    size={32}
                    className="attendance-upload__spinner"
                  />
                  <Typography
                    type="base_medium"
                    text={analyzingMessage}
                    weight="bold"
                  />
                  <Typography
                    type="small_regular"
                    text={analyzingHint}
                    className="attendance-upload__hint"
                  />
                </div>
              ) : (
                <div className="attendance-upload__dropzone-body">
                  <div className="attendance-upload__dropzone-icon">
                    <UploadFileOutlinedIcon />
                  </div>
                  <Typography
                    type="base_medium"
                    text={
                      isDragActive
                        ? 'Release to add files'
                        : 'Drag and drop Excel files here'
                    }
                    weight="bold"
                  />
                  <Typography
                    type="small_regular"
                    text="or"
                    className="attendance-upload__hint"
                  />
                  <div
                    className="attendance-upload__browse-wrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ButtonPro
                      label="Browse files"
                      type="white"
                      htmlType="button"
                      onClick={() => open()}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="attendance-upload__meta">
              <Typography
                type="small_regular"
                text={`Supported: ${ACCEPTED_EXTENSIONS.join(', ')}`}
              />
              <Typography type="small_regular" text="Max 10 MB per file" />
            </div>

            {files.length > 0 && isLargeBatch && !isAnalyzing && (
              <div className="attendance-upload__notice">
                <Typography
                  type="small_medium"
                  text={`Large upload: ${files.length} files selected.`}
                  weight="bold"
                />
                <Typography
                  type="small_regular"
                  text="Validation may take a few minutes. Keep this tab open until the review summary appears."
                  color="gray"
                />
              </div>
            )}

            {files.length > 0 && (
              <div className="attendance-upload__file-queue">
                <Typography
                  type="xs_medium"
                  text={`Selected (${files.length})`}
                  weight="bold"
                />
                <ul className="attendance-upload__file-list">
                  {files.map((file) => (
                    <li
                      key={file.name}
                      className="attendance-upload__file-item"
                    >
                      <div className="attendance-upload__file-info">
                        <span
                          className="attendance-upload__file-icon"
                          aria-hidden
                        >
                          <InsertDriveFileOutlinedIcon fontSize="small" />
                        </span>
                        <div>
                          <Typography type="small_medium" text={file.name} />
                          <Typography
                            type="xx_small"
                            text={formatFileSize(file.size)}
                            color="gray"
                          />
                        </div>
                      </div>
                      {!isAnalyzing && (
                        <button
                          type="button"
                          className="attendance-upload__file-remove"
                          onClick={() => removeFile(file.name)}
                          aria-label={`Remove ${file.name}`}
                        >
                          <CloseIcon fontSize="small" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <footer className="attendance-upload__footer">
            <div className="attendance-upload__footer-left">
              {files.length > 0 && (
                <span className="attendance-upload__footer-meta">
                  {files.length} file{files.length === 1 ? '' : 's'} ready to
                  validate
                </span>
              )}
            </div>
            <div className="attendance-upload__actions">
              <ButtonPro
                label={isAnalyzing ? 'Validating...' : 'Continue'}
                type="primary"
                htmlType="button"
                onClick={handleContinue}
                disabled={files.length === 0 || isAnalyzing}
                isLoading={isAnalyzing}
              />
            </div>
          </footer>
        </>
      )}

      {step === 'review' && uploadResult && (
        <>
          <div className="attendance-upload__period-banner">
            <Typography
              type="base_medium"
              text={`Detected period: ${detectedPeriod}`}
              weight="bold"
            />
            <Typography
              type="small_regular"
              text={`Review expires ${new Date(uploadResult.expiresAt).toLocaleString()}`}
              color="gray"
            />
          </div>

          <div className="attendance-upload__metrics">
            {reviewMetrics.map((metric) => (
              <React.Fragment key={metric.index}>
                {renderMetricCard(metric)}
              </React.Fragment>
            ))}
          </div>

          {uploadResult.warnings.length > 0 && (
            <div className="attendance-upload__warnings">
              {uploadResult.warnings.map((warning) => (
                <Typography
                  key={warning}
                  type="small_regular"
                  text={warning}
                />
              ))}
            </div>
          )}

          <section className="attendance-upload__card">
            <div className="attendance-upload__card-header">
              <span className="attendance-upload__card-icon" aria-hidden>
                <SummarizeOutlinedIcon fontSize="small" />
              </span>
              <div>
                <Typography
                  type="heading4"
                  text="File breakdown"
                  weight="bold"
                />
                <Typography
                  type="small_regular"
                  text="Row counts and parse results for each uploaded file."
                  className="attendance-upload__card-hint"
                />
              </div>
            </div>
            <div className="attendance-upload__breakdown">
              {uploadResult.files.map((file) => (
                <div
                  key={file.fileName}
                  className="attendance-upload__breakdown-row"
                >
                  <Typography type="small_medium" text={file.fileName} />
                  <Typography
                    type="small_regular"
                    text={`${file.rowsRead} rows${
                      file.parseErrors > 0
                        ? ` · ${file.parseErrors} errors`
                        : ''
                    }`}
                    color="gray"
                  />
                </div>
              ))}
            </div>

            {(uploadResult.summary.invalidRows > 0 ||
              uploadResult.summary.duplicateInBatch > 0) && (
              <Typography
                type="small_regular"
                text={(() => {
                  const { invalidRows, duplicateInBatch } =
                    uploadResult.summary;
                  const parts: string[] = [];
                  if (invalidRows > 0) {
                    parts.push(
                      `${invalidRows} row${
                        invalidRows === 1 ? '' : 's'
                      } could not be read (missing or invalid date/time)`
                    );
                  }
                  if (duplicateInBatch > 0) {
                    parts.push(
                      `${duplicateInBatch} punch${
                        duplicateInBatch === 1 ? '' : 'es'
                      } listed more than once in the file`
                    );
                  }
                  return `${parts.join(
                    ' and '
                  )} will be skipped.`;
                })()}
                className="attendance-upload__hint-block"
                color="gray"
              />
            )}

            {uploadResult.unknownPersonnelIds.length > 0 && (
              <div className="attendance-upload__unknown">
                <Typography
                  type="base_medium"
                  text="Skipped personnel IDs"
                  weight="bold"
                />
                <Typography
                  type="small_regular"
                  text="These IDs are not registered as Employee IDs in LMS, so their punches were skipped."
                  color="gray"
                />
                <div className="attendance-upload__chips">
                  {uploadResult.unknownPersonnelIds.map((id) => (
                    <span key={id} className="attendance-upload__chip">
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {issueRows.length > 0 && (
            <section className="attendance-upload__card">
              <div className="attendance-upload__card-header">
                <span className="attendance-upload__card-icon" aria-hidden>
                  <TableChartOutlinedIcon fontSize="small" />
                </span>
                <div>
                  <Typography
                    type="heading4"
                    text="Sample rows needing attention"
                    weight="bold"
                  />
                  <Typography
                    type="small_regular"
                    text="A preview of rows that will be skipped during import."
                    className="attendance-upload__card-hint"
                  />
                </div>
              </div>
              <div className="attendance-upload__table-wrap">
                <table className="attendance-upload__table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Row</th>
                      <th>ID</th>
                      <th>Status</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issueRows.slice(0, 10).map((row) => (
                      <tr
                        key={`${row.fileName}-${row.rowNumber}-${row.rawPersonnelId}`}
                      >
                        <td>{row.fileName}</td>
                        <td>{row.rowNumber}</td>
                        <td>{row.rawPersonnelId}</td>
                        <td>
                          <span
                            className={`attendance-upload__status attendance-upload__status--${formatStatusClass(
                              row.status
                            )}`}
                          >
                            {formatStatusLabel(row.status)}
                          </span>
                        </td>
                        <td>{row.error ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <footer className="attendance-upload__footer">
            <div className="attendance-upload__footer-left">
              <span className="attendance-upload__footer-meta">
                {isConfirming
                  ? confirmingHint
                  : uploadResult.summary.validRows > 0
                    ? `${uploadResult.summary.validRows} new punch${
                        uploadResult.summary.validRows === 1 ? '' : 'es'
                      } ready to import`
                    : 'No new punches to import'}
              </span>
            </div>
            <div className="attendance-upload__actions">
              <ButtonPro
                label="Back"
                type="white"
                htmlType="button"
                onClick={handleBackToUpload}
                disabled={isConfirming}
              />
              <ButtonPro
                label={
                  isConfirming
                    ? 'Importing...'
                    : uploadResult.summary.validRows > 0
                      ? `Import ${uploadResult.summary.validRows} punch${
                          uploadResult.summary.validRows === 1 ? '' : 'es'
                        }`
                      : 'Confirm (nothing new)'
                }
                type="primary"
                htmlType="button"
                onClick={handleConfirm}
                disabled={isConfirming}
                isLoading={isConfirming}
              />
            </div>
          </footer>
        </>
      )}

      {step === 'success' && confirmResult && (
        <section className="attendance-upload__card attendance-upload__card--success">
          <span className="attendance-upload__success-dot" aria-hidden>
            ✓
          </span>
          <Typography type="heading3" text="Import complete" weight="bold" />
          <Typography
            type="base_regular"
            text={confirmResult.message}
            className="attendance-upload__subtitle"
          />
          {confirmResult.dailyRecordsProcessing && (
            <Typography
              type="small_regular"
              text="Daily attendance summaries will appear in dashboards shortly."
              className="attendance-upload__hint-block"
              color="gray"
            />
          )}

          <div className="attendance-upload__metrics attendance-upload__metrics--success">
            {renderMetricCard({
              label: 'Imported',
              value: String(confirmResult.imported),
              variant: 'new'
            })}
            {renderMetricCard({
              label: 'Skipped',
              value: String(confirmResult.duplicatesSkipped),
              variant: 'dup'
            })}
            {renderMetricCard({
              label: 'Period',
              value: formatAttendanceImportPeriod({
                periodLabel: confirmResult.detectedPeriodLabel,
                startMonth: confirmResult.detectedMonth,
                startYear: confirmResult.detectedYear,
                endMonth: confirmResult.detectedEndMonth,
                endYear: confirmResult.detectedEndYear
              }),
              variant: 'total'
            })}
          </div>

          <div className="attendance-upload__actions attendance-upload__actions--center">
            <ButtonPro
              label="Upload another batch"
              type="primary"
              htmlType="button"
              onClick={handleStartOver}
            />
          </div>
        </section>
      )}
    </div>
  );
};
